import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, authApi } from '../lib/api';
import { isNative } from '../lib/platform';
import { ForgotPassword } from './ForgotPassword';
import { PasswordInput } from './PasswordInput';
import { Modal } from './ui';

export type AuthMode = 'login' | 'register' | 'forgot';
import { useSession } from '../store/session';

declare global {
  interface Window {
    google?: { accounts: { id: { initialize: (o: object) => void; renderButton: (el: HTMLElement, o: object) => void } } };
  }
}

/**
 * Email/password sign-in + registration, plus Google Identity Services on the
 * web when VITE_GOOGLE_CLIENT_ID is set. When the user is a guest, the server
 * upgrades the guest account in place so progress carries over.
 */
export function AuthForm({ initialMode = 'register', onDone, onModeChange }: { initialMode?: 'login' | 'register'; onDone?: () => void; onModeChange?: (m: AuthMode) => void }) {
  const { t, i18n } = useTranslation();
  const signedIn = useSession((s) => s.signedIn);
  const [mode, setModeState] = useState<AuthMode>(initialMode);
  // The dialog around the form titles itself by the mode (sign in, create account, reset password).
  const setMode = (m: AuthMode) => {
    setModeState(m);
    onModeChange?.(m);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ identifier: '', email: '', username: '', password: '' });
  const googleRef = useRef<HTMLDivElement>(null);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  // Parents pass an inline onDone; read it through a ref so their re-renders don't re-init Google's button.
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!googleClientId || isNative || !googleRef.current) return;
    const init = () => {
      window.google?.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (r: { credential: string }) => {
          try {
            signedIn(await authApi.oauth('google', r.credential, i18n.language));
            doneRef.current?.();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : t('errors.generic'));
          }
        },
      });
      if (googleRef.current) window.google?.accounts.id.renderButton(googleRef.current, { theme: 'filled_black', size: 'large', shape: 'pill', width: 300 });
    };
    if (window.google) init();
    else {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = init;
      document.head.appendChild(s);
    }
  }, [googleClientId, i18n.language, signedIn, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user =
        mode === 'login'
          ? await authApi.login(form.identifier, form.password)
          : await authApi.register({ email: form.email, password: form.password, username: form.username, locale: i18n.language });
      signedIn(user);
      onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? (i18n.exists(`errors.${err.code}`) && err.code !== 'bad_request' && err.code !== 'conflict' ? t(`errors.${err.code}`) : err.message) : t('errors.network'));
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  if (mode === 'forgot') return <ForgotPassword initialEmail={form.identifier} onBack={() => setMode('login')} />;

  return (
    <form className="stack" onSubmit={submit} noValidate>
      {mode === 'login' ? (
        <div className="field">
          <label htmlFor="identifier">{t('auth.identifier')}</label>
          <input id="identifier" className="input" autoFocus autoComplete="username" value={form.identifier} onChange={set('identifier')} required />
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input id="email" className="input" autoFocus type="email" autoComplete="email" value={form.email} onChange={set('email')} required />
          </div>
          <div className="field">
            <label htmlFor="username">{t('auth.username')}</label>
            <input id="username" className="input" autoComplete="username" value={form.username} onChange={set('username')} pattern="[A-Za-z0-9_]{3,20}" required />
            <span className="hint">{t('auth.usernameHint')}</span>
          </div>
        </>
      )}
      <div className="field">
        <label htmlFor="password">{t('auth.password')}</label>
        <PasswordInput id="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={form.password} onChange={set('password')} minLength={8} required />
        {mode === 'register' && <span className="hint">{t('auth.passwordHint')}</span>}
        {mode === 'login' && (
          <button type="button" className="link-button" onClick={() => setMode('forgot')}>
            {t('auth.forgotLink')}
          </button>
        )}
      </div>
      {error && <div className="error-text" role="alert">{error}</div>}
      <button className="btn primary lg block" disabled={busy}>
        {busy ? <span className="spin" /> : mode === 'login' ? t('auth.submitLogin') : t('auth.submitRegister')}
      </button>
      {googleClientId && !isNative && (
        <>
          <div className="faint center">{t('common.or')}</div>
          {/* Light color-scheme here: under our dark root scheme the browser paints Google's iframe an opaque white box.
              Fixed height: Google's iframe is briefly ~80px tall while it loads, which made the centred modal jump. */}
          <div ref={googleRef} style={{ display: 'flex', justifyContent: 'center', height: 44, overflow: 'hidden', colorScheme: 'light' }} />
        </>
      )}
      <button type="button" className="btn ghost block" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
        {mode === 'login' ? t('auth.switchToRegister') : t('auth.switchToLogin')}
      </button>
    </form>
  );
}

/**
 * The sign-in dialog: titled by what the form is doing (creating an account, signing in, resetting a
 * password). `registerTitle` and `intro` fit the place it opens from; the intro shows only for sign-up.
 */
export function AuthModal({
  initialMode = 'register',
  registerTitle,
  intro,
  onClose,
}: {
  initialMode?: 'login' | 'register';
  registerTitle?: string;
  intro?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const title = mode === 'forgot' ? t('auth.forgot.title') : mode === 'login' ? t('auth.signInTitle') : (registerTitle ?? t('auth.registerTitle'));
  return (
    <Modal title={title} onClose={onClose}>
      {intro && mode === 'register' && <p className="muted" style={{ marginTop: 0 }}>{intro}</p>}
      <AuthForm initialMode={initialMode} onDone={onClose} onModeChange={setMode} />
    </Modal>
  );
}
